/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useDisclosure,
    ModalOverlay,
    FormControl,
    FormLabel,
    FormErrorMessage,
    useColorModeValue,
    Input,
    Textarea,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    Text,
    Flex,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    useToast,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
    MdPerson,
    MdAddCircleOutline,
    MdArrowCircleLeft,
} from "react-icons/md";
import { DeleteIcon, InfoIcon } from "@chakra-ui/icons";
import { useParams, useNavigate } from "react-router-dom";
import {
    useCreatePatientVisitMutation,
    useDeletePatientVisitMutation,
    useGetVisitsByPatientIdQuery,
    useGetCurrentUserQuery,
    useGetPatientQuery,
    useGetUsersQuery,
    useGetPatientVisitQuery,
} from "store";
import moment from "moment";
import { differenceInYears } from "date-fns";
import PageTitle from "components/title";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const { patientId } = useParams();

    const { data: visits, isFetching, error } = useGetVisitsByPatientIdQuery(
        { patientId, page: pageIndex + 1, pageSize },
        { keepPreviousData: true }
    );

    // Safely handle undefined visits and default to last page as 0
    const lastPage =
        visits && visits.count ? Math.ceil(visits.count / pageSize) - 1 : 0;
    const currentPage = pageIndex + 1;

    // if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    return (
        <Box overflowX="auto">
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {visits?.results?.length > 0 ? (
                        visits.results.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No visits available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Visits() {
    const { patientId } = useParams();
    const { data: visits, error, isLoading } = useGetVisitsByPatientIdQuery({
        patientId,
        page: 1,
        pageSize: 10,
    });
    const { data: patient, isLoading: isLoadingPatient } = useGetPatientQuery(patientId);
    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();
    const visitCount = visits ? visits.count : 0;
    const lastVisit = visits?.results?.[visits.results.length - 1];
    const {
        data: currentUser,
        error: err,
        isLoading: isFetching,
    } = useGetCurrentUserQuery();
    const [visitToDelete, setVisitToDelete] = useState(null);
    const [
        deleteVisit,
        { isLoading: isDeleting },
    ] = useDeletePatientVisitMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [createVisit, results] = useCreatePatientVisitMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm();
    const navigate = useNavigate();
    const toast = useToast();

    const handleDetailsClick = (visitId) => {
        navigate(`${visitId}/details`);
    };

    const handleBacktoPatientDetails = () => {
        navigate(`/patients/${patientId}/details`);
    };

    const handleDeleteClick = (visitId) => {
        setVisitToDelete(visitId);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (!visitToDelete) return;

            // if your mutation takes an object: { patientId, visitId }
            await deleteVisit({ patientId, visitId: visitToDelete }).unwrap();

            toast({
                title: "Visit deleted",
                description: "The visit has been removed.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            setVisitToDelete(null);
            onClose();
        } catch (error) {
            toast({
                title: "Error deleting visit",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "Could not delete visit.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const onSubmit = async (data) => {
        try {
            // fire the mutation and throw on HTTP error
            await createVisit({ patientId, newVisit: { ...data } }).unwrap();

            toast({
                title: "Visit created",
                description: "Your new visit has been recorded.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            reset();
            handleClose();
        } catch (err) {
            // err.data will hold your DRF validation errors
            const apiErrors = err?.data;

            // If DRF returned field‐level errors, show them:
            const fieldMessages =
                apiErrors &&
                Object.entries(apiErrors)
                    .filter(([_, v]) => Array.isArray(v))
                    .map(
                        ([field, messages]) => `${field}: ${messages.join(" ")}`
                    )
                    .join("\n");

            toast({
                title: "Error creating visit",
                description:
                    fieldMessages ||
                    apiErrors?.detail ||
                    "An unexpected error occurred – please try again.",
                status: "error",
                duration: 7000,
                isClosable: true,
            });
        }
    };

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // useEffect(() => {
    //     if (isFetching) {
    //         console.log("Loading current user data...");
    //     }

    //     if (err) {
    //         console.error("Error fetching current user:", err);
    //     }

    //     if (currentUser) {
    //         console.log("Current User:", currentUser);
    //         console.log("Current User ID:", currentUser.id);
    //     }
    // }, [currentUser, err, isFetching]);

    

    // Log users data for debugging
    // console.log("Fetched users:", users);

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            // Make sure you access the results array correctly.
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]); // Make sure 'users' contains the fetched data array.

    // console.log("User Map:", userMap);

    const columns = useMemo(
        () => [
            {
                id: "date",
                header: "Date",
                accessor: (row) => moment(row.date).format("MMMM D, YYYY"), // Assuming row.date is the correct path
            },
            {
                id: "time",
                header: "Time",
                accessor: (row) =>
                    moment(row.time, "HH:mm:ss.SSSSSS").format("hh:mm A"), // Assuming row.time is the correct path
            },
            {
                id: "reason",
                header: "Reason",
                accessor: (row) => row.reason, // Directly accessing the reason
            },
            {
                id: "registered_by",
                header: "Registered By",
                accessor: (row) => {
                    const username = userMap[String(row.registered_by)]; // Accessing from userMap directly
                    return username || "Unknown User";
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <div>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr={2}
                        />
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                            isLoading={isDeleting}
                        />
                    </div>
                ),
            },
        ],
        [userMap, isDeleting]
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");

    return (
        <>
            <PageTitle title="Patient Visits" />
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon
                                        w="32px"
                                        h="32px"
                                        as={MdPerson}
                                        color={brandColor}
                                    />
                                }
                            />
                        }
                        name="Total Visits"
                        value={
                            isLoading ? (
                                <Spinner size="lg" />
                            ) : err ? (
                                <Text>Error fetching visits</Text>
                            ) : (
                                visitCount
                            )
                        }
                    />
                </SimpleGrid>

                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this visit?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                <Flex justify="flex-end" mb="4">
                    <Button
                        leftIcon={<MdAddCircleOutline />}
                        isLoading={results.isLoading}
                        onClick={handleShow}
                    >
                        New Visit
                    </Button>

                    <Button
                        leftIcon={<MdArrowCircleLeft />}
                        ml="4"
                        onClick={handleBacktoPatientDetails}
                    >
                        Back to Patient Details
                    </Button>
                </Flex>

                {/* Create modal form */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...visits}
                    closeOnOverlayClick={false}
                    closeOnEsc={false}
                    scrollBehavior={scrollBehavior}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>
                            New Visit
                            {patient && (
                                <Text fontSize="sm" color="gray.500" mt={2}>
                                    Patient: {patient.name} {patient.surname} | Age: { differenceInYears(new Date(), new Date(patient.date_of_birth))} | Weight: {patient.weight} lbs
                                </Text>
                            )}
                        </ModalHeader>
                        <ModalCloseButton />

                        <ModalBody>
                            {isLoading ? (
                                <Spinner size="sm" />
                            ) : error ? (
                                <Text color="red.500">
                                    Failed to load last visit.
                                </Text>
                            ) : lastVisit ? (
                                <Box
                                    // keep your existing styling…
                                    borderWidth="1px"
                                    borderRadius="md"
                                    p={4}
                                    mb={4}
                                    bg={useColorModeValue(
                                        "gray.50",
                                        "gray.800"
                                    )}
                                    boxShadow="sm"
                                    // …plus these two:
                                    maxH="60vh" // never grow past 60% of the viewport height
                                    overflowY="auto" // scroll internally if needed
                                >
                                    <Text
                                        fontWeight="bold"
                                        fontSize="lg"
                                        mb={2}
                                        color="blue.600"
                                        textAlign="center"
                                    >
                                        Last Visit Summary
                                    </Text>

                                    {[
                                        [
                                            "Date",
                                            new Date(
                                                lastVisit.date
                                            ).toLocaleDateString(),
                                        ],
                                        [
                                            "Time",
                                            new Date(
                                                `1970-01-01T${lastVisit.time}`
                                            ).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            }),
                                        ],
                                        ["Reason", lastVisit.reason],
                                        ["Diagnosis", lastVisit.diagnosis],
                                        ["Visit Notes", lastVisit.visit_notes],
                                        ["Treatment", lastVisit.treatment],
                                        [
                                            "Previous Weight",
                                            `${lastVisit.weight} lbs`,
                                        ],
                                        ["HEENT", lastVisit.heent],
                                        ["CNS", lastVisit.cns],
                                        ["Chest", lastVisit.chest],
                                        ["CVS", lastVisit.cvs],
                                        ["Genitalia", lastVisit.genitalia],
                                        ["Pulse", lastVisit.pulse],
                                        ["Blood Pressure", lastVisit.bp],
                                        ["SPO2", lastVisit.spo2],
                                        ["Ext", lastVisit.ext],
                                        ["Abdomen", lastVisit.abdomen],
                                    ].map(([label, value]) => (
                                        <Flex mb={2} key={label}>
                                            <Text
                                                fontWeight="medium"
                                                mr={2}
                                                flexShrink={0}
                                            >
                                                {label}:
                                            </Text>
                                            <Text
                                                flex="1"
                                                wordBreak="break-word" // wrap long text
                                                noOfLines={3} // optionally clamp long blocks to 3 lines
                                            >
                                                {value?.trim() || "N/A"}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Box>
                            ) : (
                                <Text>No previous visits found.</Text>
                            )}

                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Reason */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl isInvalid={errors.reason}>
                                            <FormLabel>Reason</FormLabel>
                                            <Textarea
                                                {...register("reason", {
                                                    required: true,
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "reason",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                            {errors.reason && (
                                                <FormErrorMessage>
                                                    Reason is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Notes */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.visit_notes}
                                        >
                                            <FormLabel>
                                                Clinical Notes
                                            </FormLabel>
                                            <Textarea
                                                {...register("visit_notes", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "visit_notes",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Diagnosis */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Diagnosis</FormLabel>
                                            <Textarea
                                                {...register("diagnosis", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "diagnosis",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Treatment */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Treatment</FormLabel>
                                            <Textarea
                                                {...register("treatment", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "treatment",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 1 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* HEENT */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>HEENT</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("heent")}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* CNS */}
                                    <Box>
                                        <FormControl isInvalid={errors.cns}>
                                            <FormLabel>CNS</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("cns")}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* SPO2 */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>SPO2</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("spo2")}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 2 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Chest */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Chest</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("chest", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "chest",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* CVS */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>CVS</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("cvs", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "cvs",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Genitalia */}
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.genitalia}
                                        >
                                            <FormLabel>Genitalia</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("genitalia")}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 3 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Weight */}
                                    <Box>
                                        <FormControl isInvalid={errors.weight}>
                                            <FormLabel>Weight (lbs)</FormLabel>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                {...register("weight", {
                                                    required: true,
                                                    maxLength: 7, // To ensure no more than 3 digits before the decimal
                                                    validate: (value) =>
                                                        /^\d{1,3}(\.\d{1,2})?$/.test(
                                                            value
                                                        ) ||
                                                        "Invalid weight format",
                                                })}
                                            />
                                            {errors.weight && (
                                                <FormErrorMessage>
                                                    Weight is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Ext */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>EXT</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("ext", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "ext",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Abdomen */}
                                    <Box>
                                        <FormControl isInvalid={errors.abdomen}>
                                            <FormLabel>Abdomen</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("abdomen")}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 4 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Pulse */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Pulse</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("pulse", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "pulse",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Relation BP */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>BP</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("bp", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "bp",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Hidden Patient Field */}
                                <Input
                                    type="hidden"
                                    {...register("patient", { required: true })} // Make it required
                                />

                                {/* Hidden Registered By Field */}
                                <Input
                                    value={currentUser?.id || ""}
                                    {...register("registered_by")}
                                    type="hidden"
                                />

                                <Center>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Center>
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    <PaginatedTable columns={columns} initialPageSize={10} />
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Visits;
